import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router';
import { resetPassword } from 'aws-amplify/auth';
import { IconAlertSquareRounded } from '@tabler/icons-react';

// Import Components
import ConfirmResetPassword from './ConfirmResetPassword/ConfirmResetPassword';

// Import your assets
import logoWhite from '../../assets/images/Logo.png';
import { useSelector } from 'react-redux';

// Reset Password validation Schema
const ResetPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address field is required'),
});

function ResetPassword() {
  const [resetStep, setResetStep] = useState('REQUEST_CODE');
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/landing');
    }
  }, [isAuthenticated, navigate]);

  const handleRequestCode = async (values, { setSubmitting, setStatus }) => {
    try {
      setUserEmail(values.email);
      const output = await resetPassword({ username: values.email });

      if (
        output.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE'
      ) {
        setResetStep('CONFIRM_RESET');
      }
    } catch (err) {
      switch (err.name) {
        case 'UserNotFoundException':
          setStatus('No account exists with this email');
          break;
        case 'LimitExceededException':
          setStatus('Too many attempts. Please try again later');
          break;
        default:
          setStatus('An unexpected error occurred. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setResetStep('REQUEST_CODE');
  };

  // If we're in the confirm reset step, render the ConfirmResetPassword component
  if (resetStep === 'CONFIRM_RESET') {
    return <ConfirmResetPassword username={userEmail} onBack={handleBack} />;
  }

  return (
    <div className="prelogin-container w-full">
      <div className="flex flex-col lg:flex-row min-h-screen">
        <div className="w-full lg:w-1/2 p-0">
          <div className="prelogin-leftside">
            <div className="container fix-height">
              <img className="logo logo-large" src={logoWhite} alt="logo white" />
              <div className="text">
                <h1 className="tagline-large">
                  Empowering healthcare providers with real-time prescription
                  coverage clarity
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 p-0 bg-white">
          <div className="prelogin-rightside">
            <div className="p-5">
              <Formik
                initialValues={{
                  email: '',
                }}
                validationSchema={ResetPasswordSchema}
                onSubmit={handleRequestCode}
              >
                {({ errors, touched, status, isSubmitting }) => (
                  <Form className="cst-form">
                    <h1 className="text-2xl font-bold mb-2 text-center">
                      Reset Password
                    </h1>
                    <p className="text-muted-foreground text-sm mb-4 text-center">
                      Enter your email address and we'll send you instructions
                      to reset your password.
                    </p>

                    <div className="mb-4">
                      <label htmlFor="email" className="block pb-2 text-sm font-semibold">
                        Email address
                      </label>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        className={`form-control ${errors.email && touched.email ? 'border-red-500' : ''}`}
                        placeholder="name@example.com"
                      />
                      {errors.email && touched.email && (
                        <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                      )}
                    </div>

                    {status && (
                      <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 mb-4 flex items-center gap-2" role="alert">
                        <IconAlertSquareRounded size={20} />
                        <span>{status}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span 
                            className="spinner-border spinner-border-sm me-2" 
                            role="status" 
                            aria-hidden="true"
                          />
                          <span>Sending...</span>
                        </>
                      ) : (
                        'Send Reset Code'
                      )}
                    </button>
                  </Form>
                )}
              </Formik>
              <div className="mt-4 text-center">
                <p className="text-muted-foreground text-sm mb-0">
                  Remember your password?{' '}
                  <Link
                    to="/"
                    className="text-primary font-medium no-underline hover:underline"
                  >
                    Login
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
